import { useState, useEffect } from "react";
import { useAuth, useUser } from "@clerk/react";
import { useNavigate, Link } from "@tanstack/react-router";
import type { RoomConfig } from "@tuit/shared-types";
import { adminListRooms } from "../api";
import { TopBar } from "../components/TopBar";
import styles from "../styles/pages/AdminRoomsPage.module.css";
import adminStyles from "../styles/components/AdminPage.module.css";

export function AdminRoomsPage() {
  const { isLoaded, userId } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const role = (user?.publicMetadata as { role?: string } | undefined)?.role;
  const isAdmin = role === "admin" || role === "editor";

  useEffect(() => {
    if (isLoaded && (!userId || !isAdmin)) {
      navigate({ to: "/" });
    }
  }, [isLoaded, userId, isAdmin, navigate]);

  const [rooms, setRooms] = useState<RoomConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    adminListRooms()
      .then(setRooms)
      .catch((err: unknown) => setLoadError(String(err)))
      .finally(() => setLoading(false));
  }, []);

  const filtered = rooms
    .filter(
      (r) =>
        !search ||
        r.id.toLowerCase().includes(search.toLowerCase()) ||
        r.title.toLowerCase().includes(search.toLowerCase()),
    )
    .sort((a, b) => (a.id === "home" ? -1 : b.id === "home" ? 1 : 0));

  if (!isLoaded || !userId || !isAdmin) return null;

  return (
    <div className={styles.page}>
      <TopBar />
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <Link to="/admin" className={styles.back}>← Admin</Link>
            <h1 className={styles.title}>Rooms</h1>
            <span className={styles.count}>{rooms.length} system rooms</span>
          </div>
          <div className={styles.headerRight}>
            <input
              className={styles.searchInput}
              placeholder="Search rooms…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className={adminStyles.panel}>
          <div className={adminStyles.panelHeader}>
            <h2 className={adminStyles.panelTitle}>
              System Rooms
              <span className={adminStyles.panelTitleMeta}>{filtered.length} shown</span>
            </h2>
          </div>
          <div className={styles.tableWrapper}>
            {loading ? (
              <div className={styles.tableMsg}>Loading…</div>
            ) : loadError ? (
              <div className={`${styles.tableMsg} ${styles.tableMsgError}`}>{loadError}</div>
            ) : filtered.length === 0 ? (
              <div className={styles.tableMsg}>
                {search ? "No rooms match your search." : "No rooms found."}
              </div>
            ) : (
              <table className={adminStyles.table}>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Title</th>
                    <th>Sources</th>
                    <th>Refresh</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((room) => (
                    <tr key={room.id}>
                      <td>
                        <code className={styles.idCell}>{room.id}</code>
                      </td>
                      <td>{room.title}</td>
                      <td className={adminStyles.tdNarrow}>{room.sourceIds.length}</td>
                      <td className={adminStyles.tdNarrow}>{room.refreshIntervalMinutes}m</td>
                      <td className={adminStyles.tdNarrow}>
                        <Link
                          to="/admin/rooms/$roomId"
                          params={{ roomId: room.id }}
                          className={styles.editLink}
                        >
                          Edit
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
